import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useStaticQuery, graphql } from "gatsby";
import {
	nodesConfigToObject,
	flattenNodes,
	isIframe,
	navDataFormat,
	findIfRestricted,
	findIfRestrictedStatic,
	getQueryNode,
	filterPrivatePageContext,
	findLocalCartItems,
	fetchFormsConfigData,
	isPrivatePath,
	getBoolFromVar,
} from "@parallelpublicworks/clf-utils";
import {
	AuthContext,
	AuthContextProvider,
} from "@parallelpublicworks/gatsby-clf/src/components/AuthContext";

export const SiteContext = createContext();

const defaultSortOptions = require("./../sortOptions.json");

/**
 * Site: A site wide wrapper to process general data and user authentication ...
 * ... and make it globally available via SiteContext
 * @param props.element: The page's DOM
 * @param props.props: Gatsby page props like pageContext, serverData, path, location etc.
 * @param props.options: Gatsby plugin options as defined in site's gatsby-config
 * @returns
 */

function SiteWrapper({ element, props, options }) {
	const [globalAlert, setGlobalAlert] = useState(null);
	return (
		<AuthContextProvider location={props.location} setGlobalAlert={setGlobalAlert}>
			<Site
				element={element}
				props={props}
				options={options}
				setGlobalAlert={setGlobalAlert}
				globalAlert={globalAlert}
			/>
		</AuthContextProvider>
	);
}

function Site({ element, props, options, setGlobalAlert, globalAlert }) {
	const configs = useMemo(
		() => nodesConfigToObject(options.nodesConfig),
		[options.nodesConfig]
	);
	const themeConfig = options.themeConfig ? options.themeConfig : {};
	const bookmarkableBundles =
		options.bookmarkableBundles && Array.isArray(options.bookmarkableBundles)
			? options.bookmarkableBundles
			: [];
	const siteKey = options.siteKey ? options.siteKey : "default";
	const { pageContext, path, location, serverData } = props;
	const _query_node = useMemo(() => getQueryNode(location.search), [location.search]);
	const _pageContext = useMemo(
		() => (_query_node ? filterPrivatePageContext(pageContext) : { ...pageContext }),
		[_query_node, pageContext]
	);
	//if page is generated on the fly
	if (Object.keys(_pageContext).length === 0) {
		_pageContext.private = isPrivatePath(location.pathname);
	}
	const authContext = useContext(AuthContext);
	const [
		{ isAuthenticated },
		{ dispatch, logout, checkAuth, isLoggedIn },
		[userData, setUserData],
	] = authContext;
	const pageData = useMemo(() => {
		return serverData ? { ..._pageContext, ...serverData } : { ..._pageContext };
	}, [_pageContext, serverData]);

	const isPrivateLayout = useMemo(
		() => isAuthenticated && pageData.private,
		[isAuthenticated, pageData.private]
	);
	const _restricted = useMemo(() => findIfRestrictedStatic(pageData), [pageData]);

	const [isRestricted, setIsRestricted] = useState(_restricted);
	const [cartItems, setCartItems] = useState(findLocalCartItems());
	const _isIframe = useMemo(() => isIframe(), [isIframe]);
	const [isInteractiveIframe, setIsInteractiveIframe] = useState(false);
	const [previewing, setPreviewing] = useState(_isIframe);
	const [formsConfig, setFormsConfig] = useState(null);

	const handleOpenAlert = (alertObject = null) => {
		setGlobalAlert(alertObject);
	};

	const handleCloseAlert = (event, reason) => {
		if (reason === "clickaway") {
			return;
		}
		setGlobalAlert(null);
	};

	const listenIsInteractiveIframe = (event) => {
		if (!isInteractiveIframe) {
			const { action } = event.data || {};
			if (action === "setInteractiveMode") {
				setIsInteractiveIframe(true);
			}
		}
	};

	const handleUrlRequest = (event) => {
		const { action } = event.data || {};
		if (action === "requestCurrentUrl") {
			console.log("Received request for current URL");
			if (typeof window !== "undefined" && window.parent) {
				try {
					const currentUrl = window.location.href;
					console.log("Sending current URL to parent:", currentUrl);

					window.parent.postMessage(
						{
							action: "currentUrl",
							url: currentUrl,
						},
						"*"
					);

					console.log("URL message sent successfully");
				} catch (error) {
					console.error("Error sending URL to parent:", error);
				}
			} else {
				console.warn("Cannot access window.parent to send URL");
			}
		}
	};

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.addEventListener("message", listenIsInteractiveIframe);
			window.addEventListener("message", handleUrlRequest);
		}
		return () => {
			if (typeof window !== "undefined") {
				window.removeEventListener("message", listenIsInteractiveIframe);
				window.removeEventListener("message", handleUrlRequest);
			}
		};
	}, [isInteractiveIframe]);

	useEffect(() => {
		if (
			isAuthenticated &&
			typeof window !== "undefined" &&
			window.parent &&
			!location.pathname.includes("oauth")
		) {
			window.parent.postMessage(
				{
					action: "iframeReady",
				},
				"*"
			);
		}
	}, [isAuthenticated]);

	const generalSettings = pageData?.generalSettings || {};
	const twitterUsername = generalSettings.social_media?.x
		? generalSettings.social_media.x.replace(
				/https?:\/\/(www\.)?x\.com\/([^\/]+).*/,
				"$2"
		  )
		: null;

	const socialLinks = generalSettings?.social_media || null;

	const sortOptions = options.sortOptions ? options.sortOptions : defaultSortOptions;
	const scoreSortOption = sortOptions.find((option) => option.value === "_score");
	if (!scoreSortOption) {
		sortOptions.push({
			value: "_score",
			label: "Relevance",
			direction: "desc",
		});
	}

	useEffect(() => {
		if (formsConfig == null) {
			async function fetchConfig() {
				let _formsConfig = await fetchFormsConfigData();
				setFormsConfig(_formsConfig.data);
			}
			fetchConfig();
		}
	}, []);

	const data = useStaticQuery(graphql`
		query {
			site {
				siteMetadata {
					title
					subtitle
					titleAsText
					hasSearch
					hasCart
					cartRoute
					designSystem
					description
					url
					siteUrl
					drupalUrl
					privateRoute
					allowRegistration
					accentColor
					colophonText
					apiBase
					menus
					titleTemplate
					image
					apiBaseUrl
					hasPrivateSearch
				}
			}
			sitemap: allSitemapRef {
				edges {
					node {
						id
						nid
						slug
					}
				}
			}
			bundles: allBundleRef {
				edges {
					node {
						bundle
					}
				}
			}
			layouts: allLayoutRef {
				edges {
					node {
						id
						types
					}
				}
			}
			type_definitions: allTypeDefinitionRef {
				edges {
					node {
						name
						bundle
					}
				}
			}
			footer: allFooterRef {
				edges {
					node {
						id
						texts {
							value
						}
					}
				}
			}
			mainMenu: allMenuItems(
				filter: { menu_name: { eq: "main" } }
				sort: { weight: ASC }
			) {
				edges {
					node {
						menu_name
						url
						title
						id
						weight
						childrenMenuItems {
							title
							url
							id
							weight
							route {
								parameters {
									node
								}
							}
							childrenMenuItems {
								title
								url
								id
								weight
								route {
									parameters {
										node
									}
								}
							}
						}
						parent {
							id
						}
						route {
							parameters {
								node
							}
						}
					}
				}
			}
			secondaryMenu: allMenuItems(
				filter: { menu_name: { eq: "tools" } }
				sort: { weight: ASC }
			) {
				edges {
					node {
						menu_name
						url
						title
						id
						weight
						parent {
							id
						}
						route {
							parameters {
								node
							}
						}
					}
				}
			}
			footerMenu: allMenuItems(
				filter: { menu_name: { eq: "footer" } }
				sort: { weight: ASC }
			) {
				edges {
					node {
						menu_name
						url
						title
						id
						weight
						childrenMenuItems {
							title
							url
							id
							weight
							route {
								parameters {
									node
								}
							}
						}
						parent {
							id
						}
						route {
							parameters {
								node
							}
						}
					}
				}
			}
			privateMenu: allMenuItems(
				filter: { menu_name: { eq: "account" } }
				sort: { weight: ASC }
			) {
				edges {
					node {
						menu_name
						url
						title
						id
						weight
						childrenMenuItems {
							title
							url
							id
							weight
							route {
								parameters {
									node
								}
							}
							childrenMenuItems {
								title
								url
								id
								weight
								route {
									parameters {
										node
									}
								}
							}
						}
						parent {
							id
						}
						route {
							parameters {
								node
							}
						}
					}
				}
			}
		}
	`);

	/**
	 * define general site properties
	 */
	const sitemap = useMemo(
		() => flattenNodes(data.sitemap, "all", "object"),
		[data.sitemap]
	);
	const sitemap_arr = useMemo(() => flattenNodes(data.sitemap, "all"), [data.sitemap]);
	const bundles = useMemo(
		() => flattenNodes(data.bundles, ["bundle"]).map((b) => b.bundle),
		[data.bundles]
	);
	const layouts = useMemo(() => flattenNodes(data.layouts, "all"), [data.layouts]);
	const typeDefinitions = useMemo(
		() => flattenNodes(data.type_definitions, "all"),
		[data.type_definitions]
	);

	const footerData =
		data.footer.edges &&
		data.footer.edges[0] &&
		data.footer.edges[0].node &&
		data.footer.edges[0].node.texts
			? data.footer.edges[0].node.texts.filter((d) => d.value && d.value.length > 0)
			: null;

	const userSubmenu = [];
	if (data.site.siteMetadata.privateRoute) {
		if (getBoolFromVar(process.env.CLF_IS_BOOKMARK_PAGES_ENABLED)) {
			userSubmenu.push({
				title: "Bookmarks",
				path: `${data.site.siteMetadata.privateRoute}/bookmarks`,
				id: "bookmarks-link",
			});
		}
	}

	/**
	 * format menus data
	 */
	const navData = useMemo(
		() => navDataFormat(data.mainMenu, sitemap_arr),
		[data.mainMenu, sitemap_arr]
	);
	const secondaryNavData = useMemo(
		() => navDataFormat(data.secondaryMenu, sitemap_arr),
		[data.secondaryMenu, sitemap_arr]
	);
	const footerNavData = useMemo(
		() => navDataFormat(data.footerMenu, sitemap_arr),
		[data.footerMenu, sitemap_arr]
	);
	const privateNavData = useMemo(
		() => navDataFormat(data.privateMenu, sitemap_arr),
		[data.privateMenu, sitemap_arr]
	);

	/**
	 * check if visitor can see restricted page
	 */

	useEffect(() => {
		if (isRestricted) {
			(async function () {
				let _restricted = await findIfRestricted(pageData, location, authContext);
				setIsRestricted(_restricted);
			})();
		}
	}, [location]);

	/**
	 * save cart items on local storage session
	 */

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.localStorage.setItem("cart_items", JSON.stringify(cartItems));
		}
	}, [cartItems]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.defaultPrevented) {
				return;
			}

			// if key is tab, prevent default
			if (event.key === "Tab") {
				event.preventDefault();
			}

			const activeElement = document.activeElement;
			if (
				event.key === "ArrowDown" ||
				event.key === "ArrowRight" ||
				event.key === "ArrowUp" ||
				event.key === "ArrowLeft" ||
				event.key === "Tab"
			) {
				// keep arrow key behavior on inputs
				if (event.key !== "Tab") {
					// if active element is textarea
					if (activeElement.tagName === "TEXTAREA") {
						activeElement.focus();
						return;
					} else if (
						activeElement.tagName === "INPUT" &&
						(event.key === "ArrowRight" || event.key === "ArrowLeft")
					) {
						activeElement.focus();
						return;
					}
				}

				let direction =
					event.key === "ArrowDown" || event.key === "ArrowRight"
						? "forward"
						: "backward";

				if (event.key === "Tab") {
					direction = event.shiftKey ? "backward" : "forward";
				}

				const focusableElements =
					'a, button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

				const elementsContainer = activeElement.classList.contains("is-child")
					? activeElement.closest(".submenu")
					: document;
				const elements = Array.from(
					elementsContainer.querySelectorAll(focusableElements)
				).filter((el) => {
					return el.offsetParent !== null;
				});

				const index = Array.prototype.indexOf.call(elements, activeElement);

				if (direction === "forward") {
					const nextElement = elements[index + 1] || elements[0];
					nextElement.focus();
					event.preventDefault();
				} else if (direction === "backward") {
					const prevElement = elements[index - 1] || elements[elements.length - 1];
					prevElement.focus();
					event.preventDefault();
				}
			}
			if (
				activeElement.classList.contains("submenu-trigger") &&
				(event.key === "Enter" || event.key === " ")
			) {
				event.preventDefault();
				activeElement.click();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	return (
		<SiteContext.Provider
			value={{
				...data.site.siteMetadata,
				bookmarkableBundles,
				bundles,
				cartItems,
				configs,
				footerData,
				footerNavData,
				formsConfig,
				generalSettings,
				twitterUsername,
				socialLinks,
				isIframe: _isIframe,
				isInteractiveIframe,
				isPrivate: pageData.private,
				isPrivateLayout,
				isRestricted,
				layouts,
				location,
				navData,
				pageData,
				path,
				previewing,
				privateNavData,
				secondaryNavData,
				setCartItems,
				setPreviewing,
				siteKey,
				sitemap,
				sitemap_arr,
				sortOptions,
				themeConfig,
				type: pageData.type,
				typeDefinitions,
				userSubmenu,
				globalAlert,
				handleOpenAlert,
				handleCloseAlert,
			}}
		>
			{element}
		</SiteContext.Provider>
	);
}

export default SiteWrapper;
